import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Lightbulb, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';

export const MAX_HINTS = 3;

export const SimulationHint = ({ api, simulationId, initialUsed = 0, disabled = false }) => {
    const [used, setUsed] = useState(initialUsed);
    const [hint, setHint] = useState(null);
    const [loading, setLoading] = useState(false);
    const remaining = Math.max(0, MAX_HINTS - used);

    const requestHint = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/simulation/${simulationId}/hint`);
            setUsed(data.hints_used);
            setHint({ text: data.hint, number: data.hints_used });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not get a hint right now');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-2" data-testid="simulation-hint">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lightbulb className="h-4 w-4 shrink-0 text-amber-500" />
                    <span>Stuck? Hints never lower your score.</span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex gap-1" aria-label={`${remaining} hints remaining`} data-testid="hint-remaining-dots">
                        {Array.from({ length: MAX_HINTS }).map((_, i) => (
                            <span
                                key={i}
                                className={`h-2 w-2 rounded-full transition-colors duration-300 ${i < remaining ? 'bg-amber-500' : 'bg-muted-foreground/25'}`}
                            />
                        ))}
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={requestHint}
                        disabled={disabled || loading || remaining <= 0}
                        className="border-amber-500/40 text-amber-700 transition-colors hover:bg-amber-500/10 dark:text-amber-400"
                        data-testid="get-hint-btn"
                    >
                        {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Lightbulb className="mr-1.5 h-4 w-4" />}
                        {remaining > 0 ? `Get Hint (${remaining} left)` : 'No hints left'}
                    </Button>
                </div>
            </div>
            <AnimatePresence mode="wait">
                {hint && (
                    <motion.div
                        key={hint.number}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="relative rounded-xl border border-amber-500/40 bg-amber-50 p-4 pr-10 shadow-sm dark:bg-amber-500/10"
                        data-testid="hint-card"
                    >
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                            Hint {hint.number} of {MAX_HINTS}
                        </p>
                        <p className="text-sm leading-relaxed text-foreground" data-testid="hint-text">{hint.text}</p>
                        <button
                            onClick={() => setHint(null)}
                            className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-amber-500/10 hover:text-foreground"
                            aria-label="Close hint"
                            data-testid="close-hint-btn"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
